import React, { useState, useEffect } from 'react';
import { OrderClientMetadata, PaymentMethod } from '../types';
import { apiService } from '../services/api';
import { formatOrderIdForDisplay, getDisplayAddress } from '../shared/orderUtils';
import { formatDiscountBreakdown } from '../shared/discount';
import { formatLondonDateTime, londonDateKey } from '../shared/dateUtils';
import './Orders.css';

interface Order {
    id: string;
    customerName: string;
    customerEmail: string;
    customerPhone: string;
    address: string;
    postcode?: string;
    number?: string;
    street?: string;
    area?: string;
    zone?: string;
    isPostcodeFound?: boolean;
    addressId?: string;
    items: OrderItem[];
    subtotal: number;
    discountCode?: string | null;
    discountPercentage?: number | null;
    discountFixedAmount?: number | null;
    discountAmount?: number | null;
    discountMessage?: string | null;
    total: number;
    status: 'draft' | 'created' | 'canceled' | 'delivered';
    orderDate: string;
    deliveryDate: string;
    deliverNow: boolean;
    paymentMethod: PaymentMethod;
    deliveryNotes?: string;
    driverTipAdded?: boolean;
    driverTipAmount?: number;
    receiveEmailUpdates?: boolean;
    isTest?: boolean;
    isPaid?: boolean;
    refundAmount?: number;
    sumupCheckoutId?: string | null;
    stripeSessionId?: string | null;
    stripeClientSecret?: string | null;
    clientMetadata?: OrderClientMetadata | null;
    // Time slot booking fields
    scheduledSlot?: string | null;
    slotWasBumped?: boolean;
    originalRequestedSlot?: string | null;
}

interface OrderItem {
    productId: string;
    name: string;
    quantity: number;
    price: number;
    selectedOptions?: SelectedOption[];
}

interface SelectedOption {
    optionId: string;
    optionName?: string;
    choiceIds: string[];
    choiceNames?: string[];
}

type DateFilter = 'past' | 'today' | 'future';

const calculateOrderFinancials = (order: Order) => {
    const tip = Math.max(Number(order.driverTipAmount ?? 0), 0);
    const subtotalFromOrder = Number(order.subtotal);
    const subtotalFallback = order.items.reduce((sum, item) => {
        const price = Number(item.price) || 0;
        const quantity = Number(item.quantity) || 0;
        return sum + price * quantity;
    }, 0);
    const subtotal = Number.isFinite(subtotalFromOrder) && subtotalFromOrder >= 0
        ? Number(subtotalFromOrder.toFixed(2))
        : Number(subtotalFallback.toFixed(2));
    const discountAmount = Number(order.discountAmount ?? 0) || 0;
    const normalizedDiscount = discountAmount > 0 ? Number(discountAmount.toFixed(2)) : 0;
    const normalizedTip = tip > 0 ? Number(tip.toFixed(2)) : 0;
    const totalFromOrder = Number(order.total);
    const normalizedTotal = Number.isFinite(totalFromOrder) && totalFromOrder >= 0
        ? Number(totalFromOrder.toFixed(2))
        : Number((subtotal - normalizedDiscount + normalizedTip).toFixed(2));

    return {
        subtotal,
        discount: normalizedDiscount,
        tip: normalizedTip,
        total: normalizedTotal
    };
};

const Orders: React.FC = () => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<string>('all');
    const [selectedDateFilter, setSelectedDateFilter] = useState<DateFilter>('today'); const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState<string | null>(null);
    const [isDeletingOrder, setIsDeletingOrder] = useState<string | null>(null);
    const [showMobileModal, setShowMobileModal] = useState(false);

    // Status options for filtering and updating
    const statusOptions = [
        { value: 'all', label: 'All Orders', color: '#6b7280' },
        { value: 'draft', label: 'Awaiting Payment', color: '#f59e0b' },
        { value: 'created', label: 'Ready to Prepare', color: '#3b82f6' },
        { value: 'preparing', label: 'Preparing', color: '#10b981' },
        { value: 'ready_to_deliver', label: 'Ready to Deliver', color: '#8b5cf6' },
        { value: 'delivered', label: 'Delivered', color: '#6b7280' },
        { value: 'canceled', label: 'Canceled', color: '#ef4444' }
    ];

    const statusLabelMap = statusOptions.reduce<Record<string, string>>((acc, option) => {
        acc[option.value] = option.label;
        return acc;
    }, {});

    // Date filter options
    const dateFilterOptions = [
        { value: 'past', label: 'Past Orders' },
        { value: 'today', label: 'Today' },
        { value: 'future', label: 'Future Orders' }
    ];

    const fetchOrders = async () => {
        try {
            setLoading(true);
            setError(null);

            const data = await apiService.getOrders();
            setOrders(data as Order[]);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch orders');
            console.error('Orders fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Filter orders based on date and status
    useEffect(() => {
        const today = londonDateKey();

        let filtered = orders.filter(order => {
            // Status filter
            if (selectedStatus !== 'all' && order.status !== selectedStatus) {
                return false;
            }

            // Date filter - use deliveryDate if available, otherwise orderDate.
            // Stored values are UTC ISO; compare in Europe/London.
            const filterDate = order.deliveryDate || order.orderDate;
            const orderDateOnly = londonDateKey(new Date(filterDate));

            switch (selectedDateFilter) {
                case 'past':
                    return orderDateOnly < today;
                case 'today':
                    return orderDateOnly === today;
                case 'future':
                    return orderDateOnly > today;
                default:
                    return true;
            }
        });

        setFilteredOrders(filtered);

        // If selected order is no longer in filtered list, clear selection
        if (selectedOrder && !filtered.find(o => o.id === selectedOrder.id)) {
            setSelectedOrder(null);
        }
    }, [orders, selectedStatus, selectedDateFilter, selectedOrder]);

    useEffect(() => {
        fetchOrders();
    }, []);

    const handleStatusChange = async (orderId: string, newStatus: string) => {
        try {
            setIsUpdatingStatus(orderId);

            // Note: This PUT request is public (no auth required) as it's used after payment
            // But we keep it as fetch since we don't have a dedicated API service method
            const response = await fetch(`/api/orders/${orderId}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ status: newStatus }),
            });

            if (!response.ok) {
                throw new Error(`Failed to update order status: ${response.status}`);
            }

            // Update local state
            setOrders(prevOrders =>
                prevOrders.map(order =>
                    order.id === orderId
                        ? { ...order, status: newStatus as Order['status'] }
                        : order
                )
            );

            // Update selected order if it's the one being updated
            if (selectedOrder && selectedOrder.id === orderId) {
                setSelectedOrder(prev => prev ? { ...prev, status: newStatus as Order['status'] } : null);
            }

        } catch (err) {
            console.error('Status update error:', err);
            setError(err instanceof Error ? err.message : 'Failed to update order status');
        } finally {
            setIsUpdatingStatus(null);
        }
    };

    const handleDeleteOrder = async (orderId: string) => {
        if (!confirm('Are you sure you want to permanently delete this order? This action cannot be undone.')) {
            return;
        }

        try {
            setIsDeletingOrder(orderId);
            setError(null);

            await apiService.deleteOrder(orderId);

            // Remove from local state
            setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));

            // Clear selected order if it's the one being deleted
            if (selectedOrder && selectedOrder.id === orderId) {
                setSelectedOrder(null);
                setShowMobileModal(false);
            }

        } catch (err) {
            console.error('Delete order error:', err);
            setError(err instanceof Error ? err.message : 'Failed to delete order');
        } finally {
            setIsDeletingOrder(null);
        }
    };

    const handleOrderSelect = (order: Order) => {
        setSelectedOrder(order);
        // Show modal on mobile devices
        if (window.innerWidth <= 768) {
            setShowMobileModal(true);
        }
    };

    const closeMobileModal = () => {
        setShowMobileModal(false);
    };

    // Handle window resize to close mobile modal if resizing to desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 768 && showMobileModal) {
                setShowMobileModal(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [showMobileModal]);

    const formatDate = (dateString: string) => {
        return formatLondonDateTime(dateString, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount: number) => {
        const safeAmount = Number(amount) || 0;
        return `£${safeAmount.toFixed(2)}`;
    };

    const getStatusColor = (status: string) => {
        const option = statusOptions.find(opt => opt.value === status);
        return option ? option.color : '#6b7280';
    };

    const paymentMethodLabels: Record<PaymentMethod, string> = {
        card_online: 'Card (online)',
        card_on_delivery: 'Card at the door',
        cash_on_delivery: 'Cash at the door'
    };

    const formatPaymentMethod = (method: PaymentMethod) => paymentMethodLabels[method] || method;

    const renderSlotInfo = (order: Order): JSX.Element | null => {
        if (!order.scheduledSlot && !order.slotWasBumped) {
            return null;
        }
        return (
            <>
                {order.scheduledSlot && (
                    <p><strong>🕐 Planned Delivery Time:</strong> {order.scheduledSlot}</p>
                )}
                {order.slotWasBumped && (
                    <p className="slot-bumped-warning">
                        <strong>⚠️ Slot Bumped:</strong> {order.deliverNow
                            ? `Next slot was full, moved to ${order.scheduledSlot}`
                            : `Originally requested ${order.originalRequestedSlot}, moved to ${order.scheduledSlot}`
                        }
                    </p>
                )}
            </>
        );
    };

    const renderClientMetadata = (metadata?: OrderClientMetadata | null): JSX.Element => {
        if (!metadata) {
            return <p className="metadata-empty">No client metadata captured for this order.</p>;
        }

        const identityCreatedAt = metadata.identityCreatedAt ? formatDate(metadata.identityCreatedAt) : null;
        const identityLastSeenAt = metadata.identityLastSeenAt ? formatDate(metadata.identityLastSeenAt) : null;
        const identityVisitCount = typeof metadata.identityVisitCount === 'number' && Number.isFinite(metadata.identityVisitCount)
            ? Math.max(0, Math.floor(metadata.identityVisitCount))
            : null;
        const languages = metadata.languages && metadata.languages.length > 0
            ? metadata.languages.join(', ')
            : null;
        const server = metadata.server ?? {};

        const serverLines: JSX.Element[] = [];

        if (server.ipAddress) {
            serverLines.push(<p key="ip"><strong>IP Address:</strong> {server.ipAddress}</p>);
        }

        if (server.country || server.region || server.city) {
            const locationParts = [server.city, server.region, server.country].filter(Boolean).join(', ');
            if (locationParts) {
                serverLines.push(<p key="location"><strong>Location:</strong> {locationParts}</p>);
            }
        }

        if (server.postalCode) {
            serverLines.push(<p key="postal"><strong>Postal Code:</strong> {server.postalCode}</p>);
        }

        if (server.timezone) {
            serverLines.push(<p key="tz"><strong>Edge Timezone:</strong> {server.timezone}</p>);
        }

        if (server.asn !== undefined) {
            serverLines.push(<p key="asn"><strong>ASN:</strong> {server.asn}</p>);
        }

        if (server.colo) {
            serverLines.push(<p key="colo"><strong>Colo:</strong> {server.colo}</p>);
        }

        if (server.cfRay) {
            serverLines.push(<p key="cfray"><strong>CF-Ray:</strong> {server.cfRay}</p>);
        }

        if (server.userAgent && server.userAgent !== metadata.userAgent) {
            serverLines.push(
                <div key="serverUa" className="metadata-user-agent">
                    <strong>Server User Agent:</strong>
                    <code>{server.userAgent}</code>
                </div>
            );
        }

        return (
            <div className="client-metadata-grid">
                <p><strong>Visitor ID:</strong> {metadata.visitorId}</p>
                {identityCreatedAt && <p><strong>First Seen:</strong> {identityCreatedAt}</p>}
                {identityLastSeenAt && <p><strong>Last Seen:</strong> {identityLastSeenAt}</p>}
                {identityVisitCount !== null && <p><strong>Visit Count:</strong> {identityVisitCount}</p>}
                {metadata.timezone && <p><strong>Device Timezone:</strong> {metadata.timezone}</p>}
                {metadata.platform && <p><strong>Platform:</strong> {metadata.platform}</p>}
                {metadata.hardwareConcurrency !== undefined && (
                    <p><strong>CPU Cores:</strong> {metadata.hardwareConcurrency}</p>
                )}
                {metadata.deviceMemory !== undefined && (
                    <p><strong>Device Memory:</strong> {metadata.deviceMemory} GB</p>
                )}
                {metadata.colorScheme && <p><strong>Color Scheme:</strong> {metadata.colorScheme}</p>}
                {languages && <p><strong>Languages:</strong> {languages}</p>}
                {metadata.referrer && <p><strong>Referrer:</strong> {metadata.referrer}</p>}
                {metadata.currentUrl && <p><strong>Current URL:</strong> {metadata.currentUrl}</p>}
                {metadata.screen && (
                    <p>
                        <strong>Screen:</strong> {metadata.screen.width ?? '?'}×{metadata.screen.height ?? '?'}px
                        {metadata.screen.pixelRatio ? ` @${metadata.screen.pixelRatio}x` : ''}
                    </p>
                )}
                {metadata.viewport && (
                    <p>
                        <strong>Viewport:</strong> {metadata.viewport.width ?? '?'}×{metadata.viewport.height ?? '?'}px
                    </p>
                )}
                {metadata.userAgent && (
                    <div className="metadata-user-agent">
                        <strong>User Agent:</strong>
                        <code>{metadata.userAgent}</code>
                    </div>
                )}
                {serverLines.length > 0 && (
                    <div className="metadata-server-block">
                        <p className="metadata-subheading"><strong>Server Context</strong></p>
                        {serverLines}
                    </div>
                )}
            </div>
        );
    };

    const selectedOrderFinancials = selectedOrder ? calculateOrderFinancials(selectedOrder) : null;

    if (loading) {
        return (
            <div className="orders-page">
                <div className="orders-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading orders...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="orders-page">            <div className="orders-header">
            <h2>Order Management</h2>
            <div className="orders-controls">
                <div className="orders-filters">
                    <div className="filter-group">
                        <label>Date Filter:</label>
                        <select
                            value={selectedDateFilter}
                            onChange={(e) => setSelectedDateFilter(e.target.value as DateFilter)}
                            className="filter-select"
                        >
                            {dateFilterOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>Status Filter:</label>
                        <select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            className="filter-select"
                        >
                            {statusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <button
                    onClick={fetchOrders}
                    disabled={loading}
                    className="refresh-button"
                >
                    {loading ? '🔄' : '↻'} Refresh
                </button>
            </div>
        </div>

            <div className="orders-content">
                <div className="orders-list-panel">
                    {error && (
                        <div className="error-message">
                            <p>⚠️ {error}</p>
                        </div>
                    )}

                    {filteredOrders.length === 0 ? (
                        <div className="no-orders">
                            <div className="no-orders-icon">📝</div>
                            <p>No orders found</p>
                            <p className="no-orders-subtitle">
                                {selectedDateFilter === 'today'
                                    ? "No orders for today yet."
                                    : `No ${selectedDateFilter} orders found.`}
                            </p>
                        </div>
                    ) : (
                        <div className="orders-list">
                            {filteredOrders.map((order) => (
                                <div
                                    key={order.id}
                                    className={`order-list-item ${order.isTest ? 'test-order' : ''} ${selectedOrder?.id === order.id ? 'selected' : ''}`}
                                    onClick={() => handleOrderSelect(order)}
                                >
                                    <div className="order-info">
                                        <div className="order-id">#{formatOrderIdForDisplay(order.id)}</div>
                                        <div className="order-badge-group">
                                            {order.isTest && (
                                                <span className="order-test-chip" title="Created in development environment">
                                                    Test
                                                </span>
                                            )}
                                            <div
                                                className="order-status-badge"
                                                style={{ backgroundColor: getStatusColor(order.status) }}
                                            >
                                                {statusLabelMap[order.status] || order.status}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="order-list-info">
                                        <div className="customer-name">{order.customerName}</div>
                                        <div className="order-total">{formatCurrency(order.total)}</div>
                                    </div>
                                    <div className="order-list-meta">
                                        <div className="order-time">
                                            {formatDate(order.deliveryDate || order.orderDate)}
                                        </div>
                                        <div className="delivery-type">
                                            {order.deliverNow ? '🚀 Immediate' : '📅 Scheduled'}
                                        </div>
                                        {order.driverTipAmount && order.driverTipAmount > 0 && (
                                            <div className="order-tip-badge">
                                                Tip {formatCurrency(order.driverTipAmount)}
                                            </div>
                                        )}
                                        <a
                                            href={`/melaba/order-print/${order.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="order-print-link"
                                            title="Print order labels"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            🖨️
                                        </a>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="order-details-panel">
                    {selectedOrder ? (
                        <div className="order-details-content">
                            <div className="order-details-header">
                                <div className="order-details-title">
                                    <h3>Order #{formatOrderIdForDisplay(selectedOrder.id)}</h3>
                                    {selectedOrder.isTest && (
                                        <span className="order-test-chip detail" title="Created in development environment">
                                            Test order
                                        </span>
                                    )}
                                </div>
                                <div className="order-actions">
                                    <a
                                        href={`/melaba/order-print/${selectedOrder.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="print-order-button"
                                        title="Print order labels"
                                    >
                                        🖨️ Print
                                    </a>
                                    <select
                                        value={selectedOrder.status}
                                        onChange={(e) => handleStatusChange(selectedOrder.id, e.target.value)}
                                        disabled={isUpdatingStatus === selectedOrder.id}
                                        className="status-select"
                                    >
                                        {statusOptions.filter(opt => opt.value !== 'all').map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    {selectedOrder.status === 'canceled' && (
                                        <button
                                            onClick={() => handleDeleteOrder(selectedOrder.id)}
                                            disabled={isDeletingOrder === selectedOrder.id}
                                            className="delete-order-button"
                                            title="Permanently delete this canceled order"
                                        >
                                            {isDeletingOrder === selectedOrder.id ? 'Deleting...' : '🗑️ Delete'}
                                        </button>
                                    )}
                                    {isUpdatingStatus === selectedOrder.id && (
                                        <div className="updating-indicator">Updating...</div>
                                    )}
                                </div>
                            </div>

                            {selectedOrder.isTest && (
                                <div className="test-order-banner">
                                    <span className="test-order-icon" aria-hidden="true">⚠️</span>
                                    <div>
                                        <strong>Test order</strong>
                                        <p>This order was created in the development environment and won't affect production reporting.</p>
                                    </div>
                                </div>
                            )}

                            <div className="order-details-sections">
                                <div className="details-section">
                                    <h4>Customer Information</h4>
                                    <div className="customer-details">
                                        <p><strong>Name:</strong> {selectedOrder.customerName}</p>
                                        <p><strong>Email:</strong> {selectedOrder.customerEmail}</p>
                                        {selectedOrder.customerPhone && (
                                            <p><strong>Phone:</strong> {selectedOrder.customerPhone}</p>
                                        )}
                                        <p><strong>Address:</strong> {getDisplayAddress(selectedOrder)}</p>
                                    </div>
                                </div>

                                <div className="details-section">
                                    <h4>Order Items ({selectedOrder.items.length})</h4>
                                    <div className="order-items">
                                        {selectedOrder.items.map((item, index) => (
                                            <div key={index} className="order-item">
                                                <div className="item-info">
                                                    <div className="item-name">
                                                        {item.name} × {item.quantity}
                                                    </div>
                                                    {item.selectedOptions && item.selectedOptions.length > 0 && (
                                                        <div className="item-options">                                                            {item.selectedOptions.map((option, optIndex) => (
                                                            <div key={optIndex} className="option-group">
                                                                <span className="option-label">
                                                                    {option.optionName || `Option ${optIndex + 1}`}:
                                                                </span>
                                                                <div className="option-choices">
                                                                    {(option.choiceNames || option.choiceIds || []).map((choiceName, choiceIndex) => (
                                                                        <span key={choiceIndex} className="choice-tag">
                                                                            {choiceName}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="item-price">
                                                    {formatCurrency(item.price)}
                                                </div>
                                            </div>
                                        ))}
                                        <div className="order-total-section">
                                            <div className="total-row subtotal-row">
                                                <span className="total-label">Subtotal:</span>
                                                <span className="total-amount">{formatCurrency(selectedOrderFinancials?.subtotal ?? 0)}</span>
                                            </div>
                                            {selectedOrderFinancials && selectedOrderFinancials.discount > 0 && (
                                                <div className="total-row discount-row">
                                                    <span className="total-label">Discount:</span>
                                                    <span className="total-amount">−{formatCurrency(selectedOrderFinancials.discount)}</span>
                                                </div>
                                            )}
                                            {selectedOrderFinancials && selectedOrderFinancials.tip > 0 && (
                                                <div className="total-row tip-row">
                                                    <span className="total-label">Driver Tip:</span>
                                                    <span className="total-amount">{formatCurrency(selectedOrderFinancials.tip)}</span>
                                                </div>
                                            )}
                                            <div className="total-row grand-total-row">
                                                <span className="total-label">Total:</span>
                                                <span className="total-amount">{formatCurrency(selectedOrderFinancials?.total ?? selectedOrder.total)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="details-section">
                                    <h4>Delivery Information</h4>
                                    <div className="delivery-details">
                                        <p><strong>Order Placed:</strong> {formatDate(selectedOrder.orderDate)}</p>
                                        <p><strong>Delivery Type:</strong>
                                            <span className={`delivery-type ${selectedOrder.deliverNow ? 'immediate' : 'scheduled'}`}>
                                                {selectedOrder.deliverNow ? '  Instant' : '  Scheduled'}
                                            </span>
                                        </p>
                                        {renderSlotInfo(selectedOrder)}
                                        <p><strong>Payment Method:</strong> {formatPaymentMethod(selectedOrder.paymentMethod)}</p>
                                        <p><strong>Payment Status:</strong> {selectedOrder.isPaid ? 'Paid' : 'Unpaid'}</p>
                                        {selectedOrder.discountCode && (
                                            <div className="discount-info-box">
                                                <p><strong>🎟️ Discount Code:</strong> {selectedOrder.discountCode}{(() => { const breakdown = formatDiscountBreakdown(selectedOrder.discountFixedAmount, selectedOrder.discountPercentage); return breakdown ? ` (${breakdown})` : ''; })()}</p>
                                                {selectedOrder.discountMessage && (
                                                    <p className="discount-message"><strong>⚠️ Discount Condition:</strong> {selectedOrder.discountMessage}</p>
                                                )}
                                            </div>
                                        )}
                                        {selectedOrderFinancials && selectedOrderFinancials.discount > 0 && (
                                            <p><strong>Discount Savings:</strong> −{formatCurrency(selectedOrderFinancials.discount)}</p>
                                        )}
                                        {selectedOrder.stripeSessionId && (
                                            <p><strong>Stripe Session:</strong> {selectedOrder.stripeSessionId}</p>
                                        )}
                                        {selectedOrder.sumupCheckoutId && !selectedOrder.stripeSessionId && (
                                            <p><strong>Checkout Ref:</strong> {selectedOrder.sumupCheckoutId}</p>
                                        )}
                                        {selectedOrder.refundAmount && selectedOrder.refundAmount > 0 && (
                                            <p><strong>Refund Amount:</strong> {formatCurrency(selectedOrder.refundAmount)}</p>
                                        )}
                                        <p><strong>Driver Tip:</strong> {selectedOrderFinancials && selectedOrderFinancials.tip > 0 ? formatCurrency(selectedOrderFinancials.tip) : 'None'}</p>
                                        {!selectedOrder.deliverNow && (
                                            <p><strong>Scheduled For:</strong> {formatDate(selectedOrder.deliveryDate)}</p>
                                        )}
                                        {selectedOrder.deliveryNotes && (
                                            <div className="delivery-notes">
                                                <p><strong>Delivery Notes:</strong></p>
                                                <p className="notes-content">{selectedOrder.deliveryNotes}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="details-section">
                                    <h4>Client Metadata</h4>
                                    {renderClientMetadata(selectedOrder.clientMetadata)}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="no-selection">
                            <div className="no-selection-icon">👈</div>
                            <p>Select an order from the list to view details</p>
                        </div>)}
                </div>
            </div>

            {/* Mobile Modal */}
            {showMobileModal && selectedOrder && (
                <div className="mobile-modal-overlay" onClick={closeMobileModal}>
                    <div className="mobile-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="mobile-modal-content">
                            <h3>Order #{formatOrderIdForDisplay(selectedOrder.id)}</h3>
                            <button className="close-modal-button" onClick={closeMobileModal}>
                                ✕
                            </button>
                        </div>
                        <div className="mobile-modal-body">
                            <div className="order-actions">
                                <a
                                    href={`/melaba/order-print/${selectedOrder.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="print-order-button"
                                    title="Print order labels"
                                >
                                    🖨️ Print
                                </a>
                                <select
                                    value={selectedOrder.status}
                                    onChange={(e) => handleStatusChange(selectedOrder.id, e.target.value)}
                                    disabled={isUpdatingStatus === selectedOrder.id}
                                    className="status-select"
                                >
                                    {statusOptions.filter(opt => opt.value !== 'all').map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                {selectedOrder.status === 'canceled' && (
                                    <button
                                        onClick={() => handleDeleteOrder(selectedOrder.id)}
                                        disabled={isDeletingOrder === selectedOrder.id}
                                        className="delete-order-button"
                                        title="Permanently delete this canceled order"
                                    >
                                        {isDeletingOrder === selectedOrder.id ? 'Deleting...' : '🗑️ Delete'}
                                    </button>
                                )}
                                {isUpdatingStatus === selectedOrder.id && (
                                    <div className="updating-indicator">Updating...</div>
                                )}
                            </div>

                            <div className="order-details-sections">
                                <div className="details-section">
                                    <h4>Customer Information</h4>
                                    <div className="customer-details">
                                        <p><strong>Name:</strong> {selectedOrder.customerName}</p>
                                        <p><strong>Email:</strong> {selectedOrder.customerEmail}</p>
                                        {selectedOrder.customerPhone && (
                                            <p><strong>Phone:</strong> {selectedOrder.customerPhone}</p>
                                        )}
                                        <p><strong>Address:</strong> {getDisplayAddress(selectedOrder)}</p>
                                    </div>
                                </div>

                                <div className="details-section">
                                    <h4>Order Items ({selectedOrder.items.length})</h4>
                                    <div className="order-items">
                                        {selectedOrder.items.map((item, index) => (
                                            <div key={index} className="order-item">
                                                <div className="item-info">
                                                    <div className="item-name">
                                                        {item.name} × {item.quantity}
                                                    </div>                                                    {item.selectedOptions && item.selectedOptions.length > 0 && (
                                                        <div className="item-options">
                                                            {item.selectedOptions.map((option, optIndex) => (
                                                                <div key={optIndex} className="option-group">
                                                                    <span className="option-label">
                                                                        {option.optionName || `Option ${optIndex + 1}`}:
                                                                    </span>
                                                                    <div className="option-choices">
                                                                        {(option.choiceNames || option.choiceIds || []).map((choiceName, choiceIndex) => (
                                                                            <span key={choiceIndex} className="choice-tag">
                                                                                {choiceName}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="item-price">
                                                    {formatCurrency(item.price)}
                                                </div>
                                            </div>
                                        ))}
                                        <div className="order-total-section">
                                            <div className="total-row subtotal-row">
                                                <span className="total-label">Subtotal:</span>
                                                <span className="total-amount">{formatCurrency(selectedOrderFinancials?.subtotal ?? 0)}</span>
                                            </div>
                                            {selectedOrderFinancials && selectedOrderFinancials.discount > 0 && (
                                                <div className="total-row discount-row">
                                                    <span className="total-label">Discount:</span>
                                                    <span className="total-amount">−{formatCurrency(selectedOrderFinancials.discount)}</span>
                                                </div>
                                            )}
                                            {selectedOrderFinancials && selectedOrderFinancials.tip > 0 && (
                                                <div className="total-row tip-row">
                                                    <span className="total-label">Driver Tip:</span>
                                                    <span className="total-amount">{formatCurrency(selectedOrderFinancials.tip)}</span>
                                                </div>
                                            )}
                                            <div className="total-row grand-total-row">
                                                <span className="total-label">Total:</span>
                                                <span className="total-amount">{formatCurrency(selectedOrderFinancials?.total ?? selectedOrder.total)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="details-section">
                                    <h4>Delivery Information</h4>
                                    <div className="delivery-details">
                                        <p><strong>Order Placed:</strong> {formatDate(selectedOrder.orderDate)}</p>
                                        <p><strong>Delivery Type:</strong>
                                            <span className={`delivery-type ${selectedOrder.deliverNow ? 'immediate' : 'scheduled'}`}>
                                                {selectedOrder.deliverNow ? '  Instant' : '  Scheduled'}
                                            </span>
                                        </p>
                                        {renderSlotInfo(selectedOrder)}
                                        <p><strong>Payment Method:</strong> {formatPaymentMethod(selectedOrder.paymentMethod)}</p>
                                        <p><strong>Payment Status:</strong> {selectedOrder.isPaid ? 'Paid' : 'Unpaid'}</p>
                                        {selectedOrder.discountCode && (
                                            <div className="discount-info-box">
                                                <p><strong>🎟️ Discount Code:</strong> {selectedOrder.discountCode}{(() => { const breakdown = formatDiscountBreakdown(selectedOrder.discountFixedAmount, selectedOrder.discountPercentage); return breakdown ? ` (${breakdown})` : ''; })()}</p>
                                                {selectedOrder.discountMessage && (
                                                    <p className="discount-message"><strong>⚠️ Discount Condition:</strong> {selectedOrder.discountMessage}</p>
                                                )}
                                            </div>
                                        )}
                                        {selectedOrderFinancials && selectedOrderFinancials.discount > 0 && (
                                            <p><strong>Discount Savings:</strong> −{formatCurrency(selectedOrderFinancials.discount)}</p>
                                        )}
                                        {selectedOrder.stripeSessionId && (
                                            <p><strong>Stripe Session:</strong> {selectedOrder.stripeSessionId}</p>
                                        )}
                                        {selectedOrder.sumupCheckoutId && !selectedOrder.stripeSessionId && (
                                            <p><strong>Checkout Ref:</strong> {selectedOrder.sumupCheckoutId}</p>
                                        )}
                                        {selectedOrder.refundAmount && selectedOrder.refundAmount > 0 && (
                                            <p><strong>Refund Amount:</strong> {formatCurrency(selectedOrder.refundAmount)}</p>
                                        )}
                                        <p><strong>Driver Tip:</strong> {selectedOrderFinancials && selectedOrderFinancials.tip > 0 ? formatCurrency(selectedOrderFinancials.tip) : 'None'}</p>
                                        {!selectedOrder.deliverNow && (
                                            <p><strong>Scheduled For:</strong> {formatDate(selectedOrder.deliveryDate)}</p>
                                        )}
                                        {selectedOrder.deliveryNotes && (
                                            <div className="delivery-notes">
                                                <p><strong>Delivery Notes:</strong></p>
                                                <p className="notes-content">{selectedOrder.deliveryNotes}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="details-section">
                                    <h4>Client Metadata</h4>
                                    {renderClientMetadata(selectedOrder.clientMetadata)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Orders;

